import { make } from 'vuex-pathify'
import Chat from 'twilio-chat'

import { ChatMessage } from '@/models'

const chatTokenPromises = new Map()
const chatTokens = new Map()

const videoTokenPromises = new Map()
const videoTokens = new Map()

const actionMessages = {
  bookingBegins ({ getters }) {
    let beginsIn = getters.booking.startsInHumanTime
    if (beginsIn === '0 minutes') {
      beginsIn = 'now'
    }
    const began = getters.booking.hasStarted ? 'began' : 'begins'
    return {
      id: 'bookingBegins',
      body: `Hi ${getters.user.firstName}, your booking ${began} ${beginsIn}. Make sure your video and audio are working before you join!`,
    }
  },
  timeExceeded ({ commit, getters: { booking, user } }) {
    commit('SET_TIME_EXCEEDED_SENT', true)
    return {
      body: `Hi ${user.firstName}, your booking has gone beyond the scheduled time of ${booking.duration} minutes.`,
    }
  },
  timeExceededCharge ({ commit }) {
    commit('SET_TIME_EXCEEDED_CHARGE_SENT', true)
    return {
      body: 'Just so you know - in the next release of Kintell you\'ll be able to automatically charge for extra time :)'
    }
  },
  timeExceededAdvisor ({ commit, getters: { booking } }) {
    commit('SET_TIME_EXCEEDED_SENT', true)
    return {
      body: `Hiya, your booking has gone over the scheduled duration of ${booking.duration}. Do you want to charge for this extra time?`,
      actions: [
        {
          text: 'Yes',
          callback () {
            this.disabled = true
            booking.billForOvertime = true
            booking.save()
          }
        },
        {
          text: 'No',
          callback () {
            this.disabled = true
            booking.billForOvertime = false
            booking.save()
            // TODO store unbilled time separately
          }
        }
      ]
    }
  },
  timeExceededLearner ({ commit, getters }) {
    commit('SET_TIME_EXCEEDED_SENT', true)
    const endTime = getters.booking.bookingEndTimeUtc.format('HH:mm')
    const rate = getters.booking.kintellCard.rate.toFixed(2)
    return {
      body: `Hi ${getters.user.firstName}, your booking has gone beyond the scheduled finish time of ${endTime}. The extra duration will be charged at ${getters.otherUser.firstName}’s rate of $${rate} per minute. Is this ok with you?`,
      actions: [
        {
          text: 'Yes',
          callback () {
            this.disabled = true
          }
        },
        {
          text: 'No',
          callback () {
            this.disabled = true
            // TODO end call
          }
        }
      ]
    }
  }
}

const savedSettings = typeof localStorage !== 'undefined' ? JSON.parse(localStorage.getItem('meeting.settingsv3')) : {}

const state = () => ({
  // Text chat channel
  channel: null,
  // Video call room
  room: null,
  messages: [],
  // Updates to messages should not be notified if they aren't new
  initialMessages: true,
  isInCall: false,
  partnerJoined: null,
  canJoinCall: false,
  callJoined: false,
  remoteViewRatio: null,
  timeExceededSent: false,
  timeExceededChargeSent: false,
  settings: {
    preferredVideoDevice: '',
    videoBitrate: 1500000,
    CPUThreshold: 90,
    ...savedSettings
  }
})

const getters = {
  booking (state, getters, rootState, rootGetters) {
    return rootGetters['myKintell/activeBooking']
  },
  user (state, getters, rootState, rootGetters) {
    return rootGetters['auth/user']
  },
  otherUser (state, getters) {
    const { booking, user } = getters
    return user.id === booking.advisor.id ? booking.learner : booking.advisor
  }
}

const actions = {
  getChatToken ({ state, commit, getters }) {
    const { booking } = getters

    if (chatTokens.has(booking.id)) { return chatTokens.get(booking.id) }

    if (chatTokenPromises.has(booking.id)) { return chatTokenPromises.get(booking.id) }

    const tokenPromise = (async () => {
      const response = await booking.fetchChatToken()

      chatTokens.set(booking.id, response)
      return response
    })()

    chatTokenPromises.set(booking.id, tokenPromise)

    return tokenPromise
  },
  getVideoToken ({ state, commit, getters }) {
    const { booking } = getters

    if (videoTokens.has(booking.id)) { return videoTokens.get(booking.id) }

    if (videoTokenPromises.has(booking.id)) { return videoTokenPromises.get(booking.id) }

    const tokenPromise = (async () => {
      const response = await booking.fetchVideoToken()

      videoTokens.set(booking.id, response)
      return response
    })()

    videoTokenPromises.set(booking.id, tokenPromise)

    return tokenPromise
  },
  resetVideoTokens ({ state, commit, getters }) {
    videoTokenPromises.clear()
    videoTokens.clear()
  },
  async joinChannel ({ commit, dispatch, getters }) {
    const { booking, otherUser } = getters
    const { token, currentClients } = await dispatch('getChatToken')

    // create the chat client
    const client = await Chat.create(token, {
      logLevel: 'info'
    })

    // The API has already created the channels and attached the users, we just need the reference
    const channel = await (await client.getUserChannelDescriptors()).items[0].getChannel()

    commit('SET_CHANNEL', channel)

    channel.on('messageAdded', m => dispatch('addMessage', m))
    channel.on('messageRemoved', m => dispatch('removeMessage', m))

    const partnerJoined = currentClients.includes(otherUser.id)

    this.$logger.debug('Setting partner joined ', currentClients, partnerJoined)
    commit('SET_PARTNER_JOINED', partnerJoined)

    const messages = (await channel.getMessages()).items

    let remoteViewRatio = null
    for (const message of messages) {
      if ('userJoined' in message.attributes) {
        // ignore it
      } else if ('viewRatio' in message.attributes) {
        if (message.author === otherUser.id) { remoteViewRatio = message.attributes.viewRatio }
      } else {
        await dispatch('addMessage', message)
      }
    }
    commit('SET_INITIAL_MESSAGES', false)
    commit('SET_REMOTE_VIEW_RATIO', remoteViewRatio)

    if (booking.canJoinCall) {
      commit('SET_CAN_JOIN_CALL', true)
      dispatch('addActionMessage', 'bookingBegins')
    } else {
      const startsIn = booking.$moment.duration(
        booking.bookingStartTimeUtc.diff(booking.$moment.utc())
      )
      startsIn.subtract(60, 'minutes')
      this.$logger.debug('Allowing user to join in', startsIn)
      dispatch('addActionMessage', 'bookingBegins')
      setTimeout(() => {
        commit('SET_CAN_JOIN_CALL', true)
        dispatch('removeMessage', { sid: 'bookingBegins' })
        dispatch('addActionMessage', 'bookingBegins')
      }, startsIn.asMilliseconds())
    }

    if (partnerJoined) {
      dispatch('addSystemMessage', otherUser.firstName + ' is waiting')
    }
  },
  async addMessage ({ state, getters, dispatch, commit }, message) {
    this.$logger.trace('Received message', message)
    const { otherUser } = getters
    if ('userJoined' in message.attributes) {
      if (message.author === otherUser.id) { commit('SET_PARTNER_JOINED', message.attributes.userJoined) }
      return
    } else if ('viewRatio' in message.attributes) {
      if (message.author === otherUser.id) { commit('SET_REMOTE_VIEW_RATIO', message.attributes.viewRatio) }
      return
    }
    const author = await dispatch('findUser', message.author) || 'Kintell'

    const messageOptions = {
      body: message.body,
      author,
      sent: author === getters.user,
      id: message.sid,
    }

    // if message is media type, get the media temp url
    if (message.type === 'media') {
      messageOptions.contentUrl = message.media.getContentTemporaryUrl() // the promise for getting the content URL from twilio
      // if there are any message attributes, set them
      if (message.attributes) {
        messageOptions.attributes = message.attributes
      }
      // get the filename and the type
      messageOptions.filename = message.media.state.filename
      messageOptions.isImage = message.media.state.contentType.includes('image')
    }
    commit('SET_MESSAGES', [
      ...state.messages,
      new ChatMessage(messageOptions)
    ])
  },
  removeMessage ({ state }, message) {
    const index = state.messages.findIndex(m => m.id === message.sid)
    index > -1 && state.messages.splice(index, 1)
  },
  addSystemMessage ({ commit, state }, body) {
    commit('SET_MESSAGES', [
      ...state.messages,
      new ChatMessage({ body })
    ])
  },
  addActionMessage (context, type) {
    const { commit, state } = context
    commit('SET_MESSAGES', [
      ...state.messages,
      new ChatMessage(actionMessages[type](context))
    ])
  },
  findUser ({ getters }, id) {
    return [ getters.user, getters.otherUser ]
      .find(user => id === user.id)
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations: make.mutations(state),
}
